// 메인 페이지 소개 섹션
// 좌측에 두 변호사 타일, 아래에 "Our story" 스타일 교차 타임라인.
// 타임라인 항목은 `src/data/milestones.js` 의 마일스톤 데이터로 구동되며,
// 클릭 시 MilestoneModal 이 열려 본문/갤러리/외부 링크를 보여준다.
// 사건성 마일스톤(`caseSlug` 보유) 에는 모달 안에서 "사례 보러 가기" CTA 가 노출되어
// 블로그 CASES 상세(/blog/post/:slug) 로 연결.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useInView } from '../../hooks/useInView';
import { lawyersData } from '../../data/lawyers';
import { milestones } from '../../data/milestones';
import { MilestoneModal } from './MilestoneModal';
import styles from './ExpertCredibility.module.css';

const STORY_LIMIT = 7;

function Silhouette() {
  return (
    <svg viewBox="0 0 120 160" fill="none" aria-hidden="true" className={styles.silhouette}>
      <rect width="120" height="160" fill="currentColor" opacity="0.08" />
      <circle cx="60" cy="58" r="22" fill="currentColor" opacity="0.25" />
      <path
        d="M20 160 C 20 115, 40 95, 60 95 C 80 95, 100 115, 100 160 Z"
        fill="currentColor"
        opacity="0.25"
      />
    </svg>
  );
}

function LawyerTile({ lawyer }) {
  const { ref, inView } = useInView();

  return (
    <article
      ref={ref}
      className={`${styles.tile} reveal ${inView ? 'visible' : ''} ${
        lawyer.isPlaceholder ? styles.tilePlaceholder : ''
      }`}
    >
      <div className={styles.photoWrap}>
        {lawyer.photo ? (
          <img src={lawyer.photo} alt={lawyer.photoAlt} className={styles.photo} />
        ) : (
          <div className={styles.photoFallback}>
            <Silhouette />
          </div>
        )}
        {lawyer.isPlaceholder && (
          <span className={styles.placeholderBadge}>정보 업데이트 예정</span>
        )}

        <div className={styles.photoOverlay}>
          <div className={styles.photoOverlayHead}>
            <h3 className={styles.overlayName}>{lawyer.name} 변호사</h3>
            <div className={styles.overlayRole}>{lawyer.role}</div>
          </div>
          <span className={styles.hoverHint} aria-hidden="true">
            마우스를 올려 소개 보기
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
              <path
                d="M3 8h10M9 4l4 4-4 4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </div>
      </div>

      <div className={styles.info}>
        <div className={styles.infoReveal}>
          {lawyer.landingTagline && <p className={styles.tagline}>{lawyer.landingTagline}</p>}
          {lawyer.landingNarrative && <p className={styles.narrative}>{lawyer.landingNarrative}</p>}
          <Link to={`/lawyer/${lawyer.slug}`} className={styles.cta}>
            상세 프로필 보기
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path
                d="M3 8h10M9 4l4 4-4 4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Link>
        </div>
      </div>
    </article>
  );
}

// 연도는 섹션 헤더에서 한 번에 표기. 카드별로는 추가 정보(범위)만 노출.
//   "2022~2023" + year 2022 → "2022~2023" 그대로 살림
//   "2017" + year 2017      → null (year 와 동일)
function getCardSubLabel(event) {
  const dateStr = String(event.date ?? '');
  const yearStr = String(event.year ?? '');
  if (!dateStr) return null;
  if (dateStr === yearStr) return null;
  return dateStr;
}

function StoryRow({ event, flip, onOpen }) {
  const { ref, inView } = useInView();
  const subLabel = getCardSubLabel(event);

  return (
    <li
      ref={ref}
      className={`${styles.storyRow} ${flip ? styles.storyRowFlip : ''} reveal ${
        inView ? 'visible' : ''
      }`}
    >
      <button
        type="button"
        onClick={() => onOpen(event)}
        className={styles.storyMedia}
        aria-label={`${event.year} ${event.title} — 자세히 보기`}
      >
        <img src={event.primaryImg} alt={event.title} className={styles.storyImg} />
        <span className={styles.storyMediaHint}>자세히 보기</span>
      </button>

      <div className={styles.storyBody}>
        {subLabel && <span className={styles.storyMonth}>{subLabel}</span>}
        <h4 className={styles.storyTitle}>{event.title}</h4>
        <p className={styles.storySummary}>{event.summary}</p>
        <button type="button" onClick={() => onOpen(event)} className={styles.storyMore}>
          자세히 보기
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M3 8h10M9 4l4 4-4 4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      <span className={styles.storyDot} aria-hidden="true" />
    </li>
  );
}

export function ExpertCredibility() {
  const { ref, inView } = useInView();
  const [storyExpanded, setStoryExpanded] = useState(false);
  const [activeMilestone, setActiveMilestone] = useState(null);
  const storyOver = milestones.length > STORY_LIMIT;
  const visibleMilestones =
    storyOver && !storyExpanded ? milestones.slice(0, STORY_LIMIT) : milestones;

  return (
    <section className={styles.section} id="expert">
      <div className="wrap">
        <div ref={ref} className={`${styles.header} reveal ${inView ? 'visible' : ''}`}>
          <span className={styles.eyebrow}>Expert</span>
          <h2 className={styles.title}>
            의사 출신 변호사가 직접 <span className={styles.titleAccent}>변호</span>합니다.
          </h2>
          <p className={styles.desc}>
            의학과 법, 두 전문성을 함께 갖춘 오킴스 의료팀. 진료 현장을 아는 사람이 법적 절차까지
            설계합니다.
          </p>
        </div>

        {/* ── People (작게) ── */}
        <div className={styles.peopleRow}>
          {lawyersData.map((lawyer) => (
            <LawyerTile key={lawyer.slug} lawyer={lawyer} />
          ))}
        </div>

        {/* ── Our story — alternating timeline ── */}
        <div className={styles.storyHead}>
          <span className={styles.storyEyebrow}>Our story</span>
          <h3 className={styles.storyTitleMain}>오킴스 의료팀의 발자취</h3>
          <p className={styles.storyLede}>각 마일스톤을 클릭하면 당시의 기록이 펼쳐집니다.</p>
        </div>

        <ol className={styles.story}>
          {visibleMilestones.flatMap((event, idx, arr) => {
            const showYear = idx === 0 || arr[idx - 1].year !== event.year;
            const flip = idx % 2 === 1;
            return [
              showYear && (
                <li key={`year-${event.year}`} className={styles.storyYearHeader}>
                  <span className={styles.storyYearLabel}>{event.year}</span>
                </li>
              ),
              <StoryRow key={event.id} event={event} flip={flip} onOpen={setActiveMilestone} />,
            ].filter(Boolean);
          })}
        </ol>

        <MilestoneModal milestone={activeMilestone} onClose={() => setActiveMilestone(null)} />

        {storyOver && (
          <div className={styles.storyToggleWrap}>
            <button
              type="button"
              className={styles.storyToggle}
              onClick={() => setStoryExpanded((e) => !e)}
              aria-expanded={storyExpanded}
            >
              {storyExpanded ? '접기' : `더보기 +${milestones.length - STORY_LIMIT}`}
              <svg
                className={`${styles.storyToggleCaret} ${storyExpanded ? styles.storyToggleCaretOpen : ''}`}
                width="14"
                height="14"
                viewBox="0 0 12 12"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M3 4.5l3 3 3-3"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
